import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { intakeAPI, adminAPI, appointmentAPI } from '../../lib/api';
import { 
  Queue, 
  ClipboardText, 
  Robot,
  Warning,
  Clock,
  UserCircle,
  CaretRight,
  CalendarBlank,
  CheckCircle,
  XCircle,
  ClockClockwise,
  Prohibit,
  HourglassHigh
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import WaitingRoomPanel from '../../components/staff/WaitingRoomPanel';
import { HealthAlertsPanel } from '../../components/staff/HealthAlertsPanel';

export default function CMADashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  useEffect(() => {
    loadData();
    loadPendingRequests();
  }, []);

  const loadData = async () => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        intakeAPI.getQueue(),
        adminAPI.getStats(),
      ]);
      setQueue(queueRes.data);
      setStats(statsRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await appointmentAPI.getPendingRequests();
      setPendingRequests(response.data || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading pending requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle approve reschedule
  const handleApproveReschedule = async (appointmentId) => {
    try {
      await appointmentAPI.approveReschedule(appointmentId);
      toast.success('Reschedule approved');
      loadPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reschedule');
    }
  };

  // Handle deny reschedule
  const handleDenyReschedule = async (appointmentId) => {
    try {
      await appointmentAPI.denyReschedule(appointmentId);
      toast.success('Reschedule denied');
      loadPendingRequests();
    } catch (error) {
      toast.error('Failed to deny reschedule');
    }
  };

  // Handle approve cancel
  const handleApproveCancel = async (appointmentId) => {
    try {
      await appointmentAPI.approveCancel(appointmentId);
      toast.success('Cancellation approved');
      loadPendingRequests();
    } catch (error) {
      toast.error('Failed to approve cancellation');
    }
  };

  // Handle deny cancel
  const handleDenyCancel = async (appointmentId) => {
    try {
      await appointmentAPI.denyCancel(appointmentId);
      toast.success('Cancellation denied');
      loadPendingRequests();
    } catch (error) {
      toast.error('Failed to deny cancellation');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'in_triage': return 'status-in-progress';
      case 'ready_for_provider': return 'status-completed';
      default: return 'status-pending';
    }
  };

  const getPriorityIndicator = (intake) => {
    if (intake.pain_level >= 8) return { color: 'bg-red-500', label: 'Urgent' };
    if (intake.pain_level >= 5) return { color: 'bg-amber-500', label: 'Moderate' };
    return { color: 'bg-green-500', label: 'Routine' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          CMA Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Welcome, {user?.first_name}. Manage patient intake and triage.</p>
      </div>

      <HealthAlertsPanel detailPath="/cma/health-devices" />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi-card card-hover" data-testid="cma-pending-intakes">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{queue.filter(q => q.status === 'pending').length}</div>
              <div className="kpi-label">Pending Intakes</div>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock size={24} className="text-amber-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="cma-in-triage">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{queue.filter(q => q.status === 'in_triage').length}</div>
              <div className="kpi-label">In Triage</div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ClipboardText size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="cma-ready-for-provider">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{queue.filter(q => q.status === 'ready_for_provider').length}</div>
              <div className="kpi-label">Ready for Provider</div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Queue size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="cma-total-patients">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.total_patients || 0}</div>
              <div className="kpi-label">Total Patients</div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <UserCircle size={24} className="text-teal-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Waiting Room Panel */}
        <WaitingRoomPanel />

        {/* Patient Queue */}
        <div className="card-clinical">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Patient Queue</h2>
            <button 
              onClick={() => navigate('/cma/queue')} 
              className="text-teal-600 text-sm font-medium flex items-center gap-1 hover:text-teal-700"
              data-testid="view-all-queue-btn"
            >
              View All <CaretRight size={14} />
            </button>
          </div>
        </div>
        
        {queue.length === 0 ? (
          <div className="p-12 text-center">
            <Queue size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No patients in queue</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-clinical" data-testid="patient-queue-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Chief Complaint</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Wait Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queue.slice(0, 10).map((intake) => {
                  const priority = getPriorityIndicator(intake);
                  const waitTime = Math.round((Date.now() - new Date(intake.created_at).getTime()) / (1000 * 60));
                  
                  return (
                    <tr key={intake.id} className="queue-row" data-testid={`queue-row-${intake.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <UserCircle size={18} className="text-slate-400" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{intake.patient_name || 'Patient'}</div>
                            <div className="text-xs text-slate-500">{intake.patient_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-xs truncate">{intake.chief_complaint}</td>
                      <td>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${priority.color}`} />
                          {priority.label}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusColor(intake.status)}`}>
                          {intake.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="text-slate-500">{waitTime} min</td>
                      <td>
                        <button 
                          onClick={() => navigate(`/cma/triage/${intake.id}`)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                          data-testid={`triage-btn-${intake.id}`}
                        >
                          {intake.status === 'pending' ? 'Start Triage' : 'Continue'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Pending Appointment Requests */}
      <div className="card-clinical mt-6">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <HourglassHigh size={20} className="text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Pending Appointment Requests</h2>
                <p className="text-sm text-slate-500">Reschedule and cancellation requests from patients</p>
              </div>
            </div>
            {pendingRequests.length > 0 && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                {pendingRequests.length} pending
              </span>
            )}
          </div>
        </div>

        {loadingRequests ? (
          <div className="p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-slate-500">Loading requests...</p>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarBlank size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No pending appointment requests</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pendingRequests.map((apt) => (
              <div key={apt.id} className="p-4 hover:bg-slate-50" data-testid={`pending-request-${apt.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      apt.pending_cancel ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      {apt.pending_cancel ? (
                        <Prohibit size={20} className="text-red-600" />
                      ) : (
                        <ClockClockwise size={20} className="text-amber-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{apt.patient_name}</p>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          apt.pending_cancel 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {apt.pending_cancel ? 'Cancel Request' : 'Reschedule Request'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-slate-600 mt-1">
                        <strong>Current:</strong> {apt.appointment_type?.replace(/_/g, ' ')} on{' '}
                        {new Date(apt.scheduled_time).toLocaleString()}
                      </p>
                      
                      {apt.pending_reschedule && apt.requested_time && (
                        <p className="text-sm text-teal-600 mt-1">
                          <strong>Requested new time:</strong> {new Date(apt.requested_time).toLocaleString()}
                        </p>
                      )}
                      
                      {apt.reschedule_reason && (
                        <p className="text-sm text-slate-500 mt-1">
                          <em>Reason: {apt.reschedule_reason}</em>
                        </p>
                      )}
                      
                      {apt.cancel_reason && (
                        <p className="text-sm text-slate-500 mt-1">
                          <em>Reason: {apt.cancel_reason}</em>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    {apt.pending_reschedule && (
                      <>
                        <button
                          onClick={() => handleApproveReschedule(apt.id)}
                          className="w-full px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200 flex items-center justify-center gap-1"
                          data-testid={`approve-reschedule-${apt.id}`}
                        >
                          <CheckCircle size={16} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleDenyReschedule(apt.id)}
                          className="w-full px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 flex items-center justify-center gap-1"
                          data-testid={`deny-reschedule-${apt.id}`}
                        >
                          <XCircle size={16} />
                          Deny
                        </button>
                      </>
                    )}
                    {apt.pending_cancel && (
                      <>
                        <button
                          onClick={() => handleApproveCancel(apt.id)}
                          className="w-full px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200 flex items-center justify-center gap-1"
                          data-testid={`approve-cancel-${apt.id}`}
                        >
                          <CheckCircle size={16} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleDenyCancel(apt.id)}
                          className="w-full px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 flex items-center justify-center gap-1"
                          data-testid={`deny-cancel-${apt.id}`}
                        >
                          <XCircle size={16} />
                          Deny
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
